/*
 * Colour swatches on product cards.
 *
 * Progressive enhancement over the swatch links rendered by card-swatches.liquid:
 * hovering or focusing a swatch swaps the card's primary image to that colour's
 * variant media, so shoppers can preview colourways without leaving the grid.
 *
 * Design notes:
 * - The links already point at the right variant, so with JS off a click still
 *   lands on the correct colour. Nothing here is load-bearing.
 * - The original image src/srcset are captured once and restored on mouseleave,
 *   so the swap never permanently rewrites the card.
 * - Swapping clears srcset and sets a single src: the responsive candidate set
 *   for the new colour would otherwise disagree with the pinned src and the
 *   browser could repaint the old image.
 */
// A section can load this asset more than once on the same page.
(() => {
  if (customElements.get('card-swatches')) return;

  class CardSwatches extends HTMLElement {
    connectedCallback() {
      if (this.eventsBound) return;
      this.eventsBound = true;
      this.image = this.findImage();
      if (!this.image) return;

      this.original = { src: this.image.getAttribute('src'), srcset: this.image.getAttribute('srcset') };
      this.swatches = Array.from(this.querySelectorAll('[data-swatch-media]'));
      this.originalSelection = this.querySelector('[aria-current]');

      this.onEnter = this.onEnter.bind(this);
      this.onLeave = this.onLeave.bind(this);

      this.swatches.forEach((swatch) => {
        swatch.addEventListener('mouseenter', this.onEnter);
        swatch.addEventListener('focus', this.onEnter);
      });
      // Restore when the pointer or focus leaves the whole row, not each swatch,
      // so sliding between swatches does not flicker back to the default.
      this.addEventListener('mouseleave', this.onLeave);
      this.addEventListener('focusout', this.onLeave);
    }

    disconnectedCallback() {
      this.swatches?.forEach((swatch) => {
        swatch.removeEventListener('mouseenter', this.onEnter);
        swatch.removeEventListener('focus', this.onEnter);
      });
      this.removeEventListener('mouseleave', this.onLeave);
      this.removeEventListener('focusout', this.onLeave);
      if (this.image) this.onLeave({ type: 'mouseleave' });
      this.eventsBound = false;
    }

    /* The primary card image lives in the sibling media block, not inside here. */
    findImage() {
      const card = this.closest('.card-wrapper') || this.closest('.card');
      if (!card) return null;
      const media = card.querySelector('.card__media .media');
      return media ? media.querySelector('img') : null;
    }

    onEnter(event) {
      const url = event.currentTarget.getAttribute('data-swatch-media');
      if (!url || !this.image) return;
      this.querySelectorAll('[aria-current]').forEach((link) => link.removeAttribute('aria-current'));
      event.currentTarget.setAttribute('aria-current', 'true');
      this.image.setAttribute('srcset', '');
      this.image.setAttribute('src', url);
    }

    onLeave(event) {
      // focusout fires while moving between swatches; ignore unless focus has
      // actually left the component.
      if (event.type === 'focusout' && this.contains(event.relatedTarget)) return;
      if (!this.image) return;
      this.querySelectorAll('[aria-current]').forEach((link) => link.removeAttribute('aria-current'));
      this.originalSelection?.setAttribute('aria-current', 'true');
      if (this.original.srcset) this.image.setAttribute('srcset', this.original.srcset);
      else this.image.removeAttribute('srcset');
      if (this.original.src) this.image.setAttribute('src', this.original.src);
    }
  }

  if (!customElements.get('card-swatches')) {
    customElements.define('card-swatches', CardSwatches);
  }
})();
